import { useState } from 'react';
import { type INTEGRATIONS_V2, type INTEGRATIONS } from './components/IntegrationButton';
import {
  type SaveArgs,
  type ConnectArgs,
  useSaveMutation,
  useGetIntegrationDataQuery,
  type IntegrationData
} from 'src/shared/api/integrations/factorial';
import { type MutationTrigger } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { type MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { useGetCompaniesPaginateAllQuery } from 'src/shared/api/company';
import { type Company } from 'src/shared/types/company';
import { useDispatch } from 'react-redux';
import { addToast } from 'src/old/actions';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import { useTranslation } from 'react-i18next';
import { type FactorialValues } from './pages/components/integrations2.0/Factorial/utils';
import { useA3SaveIntegration } from './pages/components/integrations2.0/A3/hooks';
import { type A3Values } from './pages/components/integrations2.0/A3/utils';
import { usePersonioSaveIntegration } from './pages/components/integrations2.0/Personio/hooks';
import { useCastillaSaveIntegration } from './pages/components/integrations2.0/Castilla/hooks';
import { type CastillaValues } from './pages/components/integrations2.0/Castilla/utils';
import { useBukSaveIntegration } from './pages/components/integrations2.0/Buk/hooks';
import { type BukValues } from './pages/components/integrations2.0/Buk/utils';
import { useBizneoSaveIntegration } from './pages/components/integrations2.0/Bizneo';
import { type BizneoValues } from './pages/components/integrations2.0/Bizneo/utils';
import { sanitizeCustomFields } from './pages/components/integrations2.0/utils/sanitizeCustomFields';

type ConnectctionCallback = MutationTrigger<MutationDefinition<any, any, any, ConnectArgs, any>>;
type ConnectIntegrationCallback = (connect: ConnectctionCallback) => (payload: ConnectArgs) => Promise<void>;
type SaveIntegrationCallback = (payload: FactorialValues) => () => Promise<void>;
type SaveIntegrationCallbackA3 = (payload: A3Values) => () => Promise<void>;
type SaveIntegrationCallbackCastilla = (payload: CastillaValues) => () => Promise<void>;
type SaveIntegrationCallbackBuk = (payload: BukValues) => () => Promise<void>;
type SaveIntegrationCallbackBizneo = (payload: BizneoValues) => () => Promise<void>;

const getCustomFields = ({ withCustomFields, customFields }: FactorialValues) =>
  withCustomFields ? sanitizeCustomFields(customFields) : undefined;

const getNotEmptyArrayOrUndefined = (values: string[] | undefined) => (values?.length ? values : undefined);

const mapValuesToPayloadByCompany = (values: FactorialValues & { companies: Company[] | undefined }): SaveArgs => ({
  id: values.id,
  subsidiary: values.company,
  legalEntityId: values.selectAllLegalEntities ? undefined : values.legalEntityId,
  leaves: getNotEmptyArrayOrUndefined(values.leaves),
  teams: getNotEmptyArrayOrUndefined(values.teams),
  customFields: getCustomFields(values)
});

const mapValuesToPayloadByCompanyForAll = (values: FactorialValues, integration: IntegrationData): SaveArgs => ({
  id: integration.id,
  subsidiary: integration.subsidiary,
  legalEntityId: values.selectAllLegalEntities ? undefined : values.legalEntityId,
  leaves: values.leaves,
  teams: values.teams,
  customFields: getCustomFields(values)
});

export type HandleSubmit =
  | HandleSubmitFactorial
  | HandleSubmitA3
  | HandleSubmitCastilla
  | HandleSubmitBuk
  | HandleSubmitBizneo;

type HandleSubmitFactorial = {
  [SUBMIT_INTEGRATION_TYPE.CONNECT]: ConnectIntegrationCallback;
  [SUBMIT_INTEGRATION_TYPE.SAVE]: SaveIntegrationCallback;
  [SUBMIT_INTEGRATION_TYPE.SAVE_FOR_ALL_SUBSIDIARIES]: SaveIntegrationCallback;
};

type HandleSubmitA3 = {
  [SUBMIT_INTEGRATION_TYPE.SAVE]: SaveIntegrationCallbackA3;
  [SUBMIT_INTEGRATION_TYPE.CONNECT]?: null;
};

type HandleSubmitCastilla = {
  [SUBMIT_INTEGRATION_TYPE.SAVE]: SaveIntegrationCallbackCastilla;
};

type HandleSubmitBuk = {
  [SUBMIT_INTEGRATION_TYPE.SAVE]: SaveIntegrationCallbackBuk;
};

type HandleSubmitBizneo = {
  [SUBMIT_INTEGRATION_TYPE.SAVE]: SaveIntegrationCallbackBizneo;
};

export enum SUBMIT_INTEGRATION_TYPE {
  CONNECT = 'connect',
  SAVE = 'save',
  SAVE_FOR_ALL_SUBSIDIARIES = 'save_for_all_subsidiaries'
}

export const NOT_SELECTED_INTEGRATION_NAME = 'none';

export const useIntegrations = (integrationNameSource: INTEGRATIONS | INTEGRATIONS_V2) => {
  const [integrationName, setIntegrationName] = useState<INTEGRATIONS | INTEGRATIONS_V2 | 'none'>(
    integrationNameSource ?? NOT_SELECTED_INTEGRATION_NAME
  );
  const [save] = useSaveMutation();
  const { data: integrationData } = useGetIntegrationDataQuery();
  const dispatch = useDispatch();
  const { data: companies } = useGetCompaniesPaginateAllQuery({ searchValue: '' });
  const saveA3IntegrationData = useA3SaveIntegration();
  const savePersonioIntegrationData = usePersonioSaveIntegration();
  const saveCastillaIntegrationData = useCastillaSaveIntegration();
  const saveBukIntegrationData = useBukSaveIntegration();
  const saveBizneoIntegrationData = useBizneoSaveIntegration();
  const { t } = useTranslation('superAdmin');

  const connectIntegration = (connect: ConnectctionCallback) => async (payload: ConnectArgs) => {
    try {
      await connect(payload);
    } catch (error) {
      console.error(error);
    }
  };

  const saveIntegrationData = (companies: Company[] | undefined) => (values: FactorialValues) => async () => {
    try {
      const payload = mapValuesToPayloadByCompany({ ...values, companies });

      const response = await save(payload).unwrap();

      if (response.data) {
        dispatch(addToast({ ...toastTypes.success, defaultMessage: t('toasts.updated') }));
      } else {
        dispatch(addToast({ ...toastTypes.error, defaultMessage: t('toasts.error') }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const saveForAll = (data: IntegrationData[] | undefined) => (values: FactorialValues) => async () => {
    try {
      if (!data) {
        return;
      }

      await Promise.all(
        data.map(async (integration: IntegrationData) => {
          try {
            const payload = mapValuesToPayloadByCompanyForAll(values, integration);

            await save(payload);

            dispatch(addToast({ ...toastTypes.success, defaultMessage: t('toasts.updated') }));
          } catch (error) {
            console.error(error);
          }
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmitFactorial: HandleSubmit = {
    [SUBMIT_INTEGRATION_TYPE.CONNECT]: (connect: ConnectctionCallback) => connectIntegration(connect),
    [SUBMIT_INTEGRATION_TYPE.SAVE]: saveIntegrationData(companies),
    [SUBMIT_INTEGRATION_TYPE.SAVE_FOR_ALL_SUBSIDIARIES]: saveForAll(integrationData)
  };

  const handleSubmitA3: HandleSubmit = {
    [SUBMIT_INTEGRATION_TYPE.SAVE]: saveA3IntegrationData()
  };

  const handleSubmitPersonio: HandleSubmit = {
    [SUBMIT_INTEGRATION_TYPE.SAVE]: savePersonioIntegrationData()
  };

  const handleSubmitCastilla: HandleSubmit = {
    [SUBMIT_INTEGRATION_TYPE.SAVE]: saveCastillaIntegrationData()
  };

  const handleSubmitBuk: HandleSubmit = {
    [SUBMIT_INTEGRATION_TYPE.SAVE]: saveBukIntegrationData()
  };

  const handleSubmitBizneo: HandleSubmit = {
    [SUBMIT_INTEGRATION_TYPE.SAVE]: saveBizneoIntegrationData()
  };

  const handleSubmitObj = {
    factorial: handleSubmitFactorial,
    a3api: handleSubmitA3,
    personio: handleSubmitPersonio,
    grupoCastillaEpsilon: handleSubmitCastilla,
    buk: handleSubmitBuk,
    bizneo: handleSubmitBizneo
  };

  const handleSubmit = handleSubmitObj[integrationName as INTEGRATIONS_V2];
  return {
    integrationName,
    setIntegrationName,
    handleSubmit
  };
};
